import { useMemo } from 'react'
import {
  Card,
  CardContent,
  makeStyles,
  Divider,
  useMediaQuery,
  Link as MuiLink,
  useTheme,
} from '@material-ui/core'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@material-ui/lab'
import theme from '../common/theme'
import SignatureModal from './SignatureModal'
import { useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'next-i18next'

const TrackingTimeline = ({ dataList, forceMobile = false }) => {
  const { t } = useTranslation('tracking')
  const theme = useTheme()
  const isMobile = forceMobile
    ? true
    : useMediaQuery(theme.breakpoints.down('xs'))
  const useStyles = makeStyles({
    dot: {
      width: 20,
      height: 20,
      boxShadow: 'none',
      backgroundColor: '#E0E0E0',
    },
    activeDot: {
      width: 25,
      height: 25,
      border: '5px solid #CCECFF',
      backgroundColor: theme.palette.primary.main,
      boxShadow: 'none',
    },
    activeText: {
      color: theme.palette.primary.main,
    },
    notActiveText: {
      color: '#BDBDBD',
    },
    statusText: {
      marginTop: 5,
    },
    timelineConnector: {
      backgroundColor: '#E0E0E0',
    },
    signature: {
      width: 200,
      height: 70,
      objectFit: 'contain',
      [theme.breakpoints.down('xs')]: {
        width: 100,
        height: 35,
      },
    },
    timelineRoot: {
      [theme.breakpoints.down('xs')]: {
        padding: 0,
      },
      '& dl': {
        margin: `${theme.spacing(0.5)}px 0`,
      },
    },
    cursorPointer: {
      cursor: 'pointer',
    },
    timelineContent: {
      paddingRight: 0,
      marginBottom: theme.spacing(3),
    },
    timelineOppositeContent: {
      paddingLeft: 0,
      marginBottom: theme.spacing(3),
    },
  })
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false)
  const classes = useStyles()
  const dateFormatter = (dateStr) => {
    const date = dayjs(dateStr)
    if (isMobile)
      return (
        <>
          {date.format('ll')}
          <br />
          {date.format('LT')}
        </>
      )
    return date.format('LLL')
  }

  const ascDataList = useMemo(() => {
    return dataList.sort(
      (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
    )
  }, [dataList])

  return (
    <>
      <Timeline classes={{ root: classes.timelineRoot }}>
        {ascDataList
          .slice(0)
          .reverse()
          .map((data, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent
                classes={{ root: classes.timelineOppositeContent }}
              >
                <span
                  className={
                    index === 0 ? classes.activeText : classes.notActiveText
                  }
                >
                  {dateFormatter(data.datetime)}
                </span>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  classes={{
                    root: index === 0 ? classes.activeDot : classes.dot,
                  }}
                />
                {index < dataList.length - 1 && (
                  <TimelineConnector
                    classes={{ root: classes.timelineConnector }}
                  />
                )}
              </TimelineSeparator>
              <TimelineContent classes={{ root: classes.timelineContent }}>
                <span className={index === 0 ? classes.activeText : ''}>
                  {data.description} <b>{data.location}</b>
                </span>
                {'receiver' in data && (
                  <dl>
                    {t('signed-by')} {data.receiver}
                  </dl>
                )}
                {'signature' in data && (
                  <>
                    <dl style={{ color: '#BDBDBD' }}>
                      {t('receiver')}{' '}
                      <MuiLink
                        className={classes.cursorPointer}
                        onClick={() => setSignatureModalOpen(true)}
                      >
                        {t('see-large-signature')}
                      </MuiLink>
                    </dl>
                    <img
                      src={data.signature}
                      alt="receivers signature"
                      className={classes.signature}
                    />
                    <SignatureModal
                      isOpen={isSignatureModalOpen}
                      setOpen={setSignatureModalOpen}
                      src={data.signature}
                    />
                  </>
                )}
              </TimelineContent>
            </TimelineItem>
          ))}
      </Timeline>
    </>
  )
}

const StatusCard = ({ data, forceMobile = false }) => {
  const { t } = useTranslation('tracking')

  const mapDataToTimelineList = (input) => {
    const output = []
    Object.values(input).map((statusObj) => {
      let processedStatusObj = {
        datetime: statusObj.datetime,
        description: statusObj.description,
        location: statusObj.location,
      }
      if (statusObj.signature)
        processedStatusObj.signature = statusObj.signature
      if (statusObj.receiver) processedStatusObj.receiver = statusObj.receiver

      output.push(processedStatusObj)
    })
    return output
  }

  const useStyles = makeStyles({
    header: {
      fontWeight: 400,
      fontSize: 22,
      margin: 8,
      ...theme.headerFont,
      color: '#404041',
    },
    divider: {
      margin: '10px -16px 30px -16px',
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    card: {
      [theme.breakpoints.up('sm')]: {
        ...theme.homeStyle.boxShadow,
      },
    },
    cardContent: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.5),
      },
    },
  })

  const useStylesMobile = makeStyles({
    header: {
      fontWeight: 400,
      fontSize: 22,
      margin: 8,
      ...theme.headerFont,
      color: '#404041',
    },
    divider: {
      margin: '10px -16px 30px -16px',
      display: 'none',
    },
    card: {},
    cardContent: {
      padding: theme.spacing(0.5),
    },
  })

  const classes = forceMobile ? useStylesMobile() : useStyles()

  return (
    <Card elevation={0} classes={{ root: classes.card }}>
      <CardContent classes={{ root: classes.cardContent }}>
        <span className={classes.header}>{t('delivery-status')}</span>
        <Divider className={classes.divider} />
        {data ? (
          <TrackingTimeline
            dataList={mapDataToTimelineList(data)}
            forceMobile={forceMobile}
          />
        ) : (
          <h5>{t('data-not-found')}</h5>
        )}
      </CardContent>
    </Card>
  )
}

export default StatusCard
